/**
 * Nachtrag-Version, Gastseite (docs/PLAN-NACHTRAG-VERSION.md, Einheit 4): reine Aufbereitung für die Anzeige.
 *
 * Es wird NICHTS Neues gerechnet. Die Summen stammen aus dem Server (`options[].amount_total_cents` der angenommenen
 * Fassung bzw. `amountTotalCents` im eingefrorenen Snapshot der Fassung N+1). Der Vergleich "was hat sich geändert" ist
 * `nachtragsVergleich` aus `@maestro/pricing` - dieselbe Funktion, die der Server benutzt. Die Zeilenbeträge sind
 * `lineGross` derselben Engine, nur zur Anzeige.
 */
import { lineGross, nachtragsVergleich, type NachtragPositionSnapshot, type NachtragsVergleich } from "@maestro/pricing";
import type { PublicOffer, PublicOfferItem } from "../ui/types";
import { wahlgaengeAus, type AngebotOption, type Wahlgang } from "../ui/angebot-auswahl-modell";

export type NachtragMarke = "neu" | "geaendert" | "entfallen" | "unveraendert";

export interface NachtragZeilenZustand {
  /** Zeilenbetrag in Cent; null = kein eigener Betrag (inklusive/optional). */
  betragCents: number | null;
  /** z. B. "65 × 60,00 €" - Menge mal Einzelpreis, so wie der Server sie eingefroren hat. */
  detail: string | null;
}

export interface NachtragZeile {
  key: string;
  name: string;
  marke: NachtragMarke;
  alt: NachtragZeilenZustand | null;
  neu: NachtragZeilenZustand | null;
}

export interface NachtragAnsicht {
  version: number;
  vergleich: NachtragsVergleich;
  gaeste: { alt: number | null; neu: number | null; geaendert: boolean };
  datum: { alt: string | null; neu: string | null; geaendert: boolean };
  positionen: NachtragZeile[];
  summe: { altCents: number; neuCents: number; differenzCents: number };
  /** Wahlgänge ("oder") der Fassung N+1, deren Gästezahl sich geändert hat oder die neu sind: nur hier muss die Kundin zuordnen. */
  wahlgaenge: Wahlgang[];
  /** Fassung N+1 als Option im Format der Gastseite (für die Wahlgang-Zuordnung). */
  neuOption: AngebotOption;
  /** Bisherige Aufteilung der geltenden Fassung (`menu_mengen`), auf die Positionen der Fassung N+1 umgeschlüsselt: Vorbelegung der Mengenfelder. */
  vorbelegung: Record<string, Record<string, number>>;
  zahlung: NonNullable<NonNullable<PublicOffer["nachtrag"]>["zahlung"]> | null;
}

type Roh = Record<string, unknown>;
const istObj = (v: unknown): v is Roh => v != null && typeof v === "object" && !Array.isArray(v);
const zahl = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const text = (v: unknown): string | null => (typeof v === "string" ? v : null);

/** Eine Position aus dem Versand-Snapshot (camelCase) im Format der Gastseite (snake_case, wie `get_public_offer` sie liefert). */
function itemAusSnapshot(i: Roh, nr: number): PublicOfferItem {
  return {
    id: String(i.id ?? `n${nr}`),
    day_no: zahl(i.dayNo),
    section: (text(i.section) ?? "menu") as PublicOfferItem["section"],
    course_label: text(i.courseLabel),
    choice_group_id: text(i.choiceGroupId),
    name: text(i.name) ?? "",
    description: text(i.description),
    qty_milli: zahl(i.qtyMilli) ?? 1000,
    unit: text(i.unit),
    unit_price_cents: zahl(i.unitPriceCents) ?? 0,
    price_mode: (text(i.priceMode) ?? "per_person") as PublicOfferItem["price_mode"],
    vat_rate_bp: zahl(i.vatRateBp) ?? 0,
    is_optional: i.isOptional === true,
    surcharge: i.surcharge === true,
    guest_count: zahl(i.guestCount),
    sort_order: zahl(i.sortOrder) ?? nr,
    package_id: text(i.packageId),
  };
}

/** Die Variante der Fassung N+1 (Snapshot) im Format der Gastseite. */
export function neueOptionAusSnapshot(snapshot: unknown): AngebotOption | null {
  const roh = Array.isArray(snapshot) ? snapshot.find(istObj) : null;
  if (!roh) return null;
  const items = (Array.isArray(roh.items) ? roh.items : []).filter(istObj).map(itemAusSnapshot);
  const tage = (Array.isArray(roh.days) ? roh.days : []).filter(istObj).map((d) => ({
    day_no: zahl(d.dayNo) ?? 1,
    label: text(d.label),
    date: text(d.date),
    guest_count: zahl(d.guestCount) ?? 0,
  }));
  return {
    id: String(roh.id ?? "nachtrag"),
    label: text(roh.label) ?? "",
    title: text(roh.title),
    guest_count: zahl(roh.guestCount) ?? 0,
    amount_total_cents: zahl(roh.amountTotalCents) ?? 0,
    menu_selection: null,
    items,
    days: tage as unknown as AngebotOption["days"],
    is_chosen: true,
    wahl: (istObj(roh.wahl) ? roh.wahl : null) as AngebotOption["wahl"],
    wahlgaenge: (istObj(roh.wahlgaenge) ? roh.wahlgaenge : null) as AngebotOption["wahlgaenge"],
  };
}

const alsPosition = (it: PublicOfferItem): NachtragPositionSnapshot => ({
  id: it.id,
  name: it.name,
  section: it.section,
  dayNo: it.day_no,
  // Die Gastseite kennt die Katalog-Kennung der angenommenen Fassung nicht; beide Seiten gruppieren darum gleich (Paket, sonst Name).
  catalogItemId: null,
  packageId: it.package_id ?? null,
  qtyMilli: it.qty_milli,
  unitPriceCents: it.unit_price_cents,
  unit: it.unit,
  isOptional: it.is_optional,
});

function gaesteFuer(it: PublicOfferItem, o: AngebotOption): number {
  const tag = it.day_no != null ? (o.days ?? []).find((d) => d.day_no === it.day_no)?.guest_count : null;
  return tag != null && tag > 0 ? tag : o.guest_count;
}

const EUR = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const euro = (c: number) => EUR.format(c / 100);
const menge = (milli: number) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 3 }).format(milli / 1000);

function zeilenZustand(it: PublicOfferItem, o: AngebotOption): NachtragZeilenZustand {
  const einzel = euro(Math.round((it.qty_milli * it.unit_price_cents) / 1000));
  if (it.is_optional) return { betragCents: null, detail: "optional" };
  let betrag: number | null = null;
  try {
    betrag = lineGross(
      { section: it.section, priceMode: it.price_mode, qtyMilli: it.qty_milli, unitPriceCents: it.unit_price_cents, vatRateBp: it.vat_rate_bp, guestCount: it.guest_count ?? null },
      gaesteFuer(it, o),
    );
  } catch {
    betrag = null;
  }
  if (betrag === 0 || betrag == null) return { betragCents: null, detail: it.unit_price_cents === 0 ? "inklusive" : null };
  const detail = it.price_mode === "per_person" ? `${it.guest_count && it.guest_count > 0 ? Math.min(it.guest_count, gaesteFuer(it, o)) : gaesteFuer(it, o)} × ${einzel}` : it.price_mode === "per_unit" ? `${menge(it.qty_milli)} × ${euro(it.unit_price_cents)}` : null;
  return { betragCents: betrag, detail };
}

const schluessel = (it: { day_no: number | null; section: string; name: string }) => `${it.day_no ?? ""}|${it.section}|${it.name.trim().toLowerCase()}`;

/**
 * Die bisherige Aufteilung (`event.menu_mengen`: Gruppe -> Position der Fassung N -> Gäste), umgeschlüsselt auf die Positionen der
 * Fassung N+1. Die Kopie kennt ihre Vorlage über `nachtragVonItemId` (Versand-Snapshot); fehlt sie, gilt dieselbe Gruppe + derselbe Name.
 * Nur Gerichte, die es in beiden Fassungen gibt, werden vorbelegt - so bleiben nur die zusätzlichen Gäste offen.
 */
function vorbelegteMengen(offer: PublicOffer, snapshot: unknown, altItems: PublicOfferItem[], wahlgaenge: Wahlgang[]): Record<string, Record<string, number>> {
  const bisher = offer.event.menu_mengen;
  const aus: Record<string, Record<string, number>> = {};
  if (!bisher || !istObj(bisher)) return aus;
  const roh = Array.isArray(snapshot) ? snapshot.find(istObj) : null;
  const rohItems = new Map<string, Roh>();
  for (const i of Array.isArray(roh?.items) ? (roh!.items as unknown[]).filter(istObj) : []) rohItems.set(String(i.id), i);
  for (const w of wahlgaenge) {
    const reihe = bisher[w.gruppeId];
    if (!reihe || !istObj(reihe)) continue;
    for (const g of w.gerichte) {
      const von = text(rohItems.get(g.id)?.nachtragVonItemId);
      const alt =
        (von ? altItems.find((a) => a.id === von) : undefined) ??
        altItems.find((a) => a.choice_group_id === w.gruppeId && a.name.trim().toLowerCase() === g.item.name.trim().toLowerCase());
      const n = alt ? reihe[alt.id] : undefined;
      if (typeof n === "number" && n > 0) (aus[w.gruppeId] ??= {})[g.id] = Math.floor(n);
    }
  }
  return aus;
}

/** Gast-Ansicht des Nachtrags aus der Antwort von `get_public_offer`; null, wenn kein (lesbarer) Nachtrag da ist. */
export function nachtragAnsicht(offer: PublicOffer): NachtragAnsicht | null {
  const n = offer.nachtrag;
  if (!n) return null;
  const neuOption = neueOptionAusSnapshot(n.snapshot);
  if (!neuOption) return null;
  const altOption = offer.options.find((o) => o.is_chosen) ?? offer.options[0];
  if (!altOption) return null;
  const evSnap = istObj(n.event_snapshot) ? n.event_snapshot : {};

  const altItems = altOption.items ?? [];
  const neuItems = neuOption.items ?? [];
  // Die angenommene Summe ist die des Vorgangs (server-gesetzt); die Variante trägt sie nur, solange nichts zugebucht ist.
  const altSumme = offer.event.amount_total_cents ?? altOption.amount_total_cents;
  const vergleich = nachtragsVergleich(
    { option: { amountTotalCents: altSumme, guestCount: altOption.guest_count, items: altItems.map(alsPosition) }, event: { guestCount: offer.event.guest_count, eventDate: offer.event.event_date } },
    {
      option: { amountTotalCents: neuOption.amount_total_cents, guestCount: neuOption.guest_count, items: neuItems.map(alsPosition) },
      event: { guestCount: zahl(evSnap.guestCount), eventDate: text(evSnap.eventDate) },
    },
  );

  // Zeilen: erst die Positionen der neuen Fassung in ihrer Reihenfolge (geändert/neu/unverändert), dann die entfallenen.
  const aenderungen = new Map<string, typeof vergleich.positionen>();
  for (const p of vergleich.positionen) {
    const k = schluessel({ day_no: p.dayNo, section: p.section ?? "", name: p.name });
    aenderungen.set(k, [...(aenderungen.get(k) ?? []), p]);
  }
  const nimm = (it: PublicOfferItem, art?: "neu" | "geaendert" | "entfallen") => {
    const liste = aenderungen.get(schluessel(it)) ?? [];
    const i = liste.findIndex((p) => (art ? p.art === art : p.art !== "entfallen"));
    if (i < 0) return null;
    const [treffer] = liste.splice(i, 1);
    return treffer ?? null;
  };
  const altNachName = new Map<string, PublicOfferItem[]>();
  for (const it of altItems) altNachName.set(schluessel(it), [...(altNachName.get(schluessel(it)) ?? []), it]);

  const positionen: NachtragZeile[] = [];
  let nr = 0;
  for (const it of [...neuItems].sort((a, b) => a.sort_order - b.sort_order)) {
    if (it.section === "adjustment") continue;
    nr += 1;
    const treffer = nimm(it);
    const altIt = (altNachName.get(schluessel(it)) ?? []).shift();
    if (treffer?.art === "neu") {
      positionen.push({ key: `n${nr}`, name: it.name, marke: "neu", alt: null, neu: zeilenZustand(it, neuOption) });
    } else if (treffer?.art === "geaendert") {
      positionen.push({ key: `n${nr}`, name: it.name, marke: "geaendert", alt: altIt ? zeilenZustand(altIt, altOption) : null, neu: zeilenZustand(it, neuOption) });
    } else {
      const neuStand = zeilenZustand(it, neuOption);
      // Unveränderte Zeilen ohne eigenen Betrag (Bestandteile eines Pakets) stehen schon im Betrag der Kopfzeile.
      if (neuStand.betragCents == null && neuStand.detail === "inklusive") continue;
      const altStand = altIt ? zeilenZustand(altIt, altOption) : neuStand;
      // Dieselbe Position, aber ein anderer Zeilenbetrag (z. B. 65 -> 75 Gäste bei gleichem Preis je Gast): für die Kundin „geändert“.
      const andererBetrag = altStand.betragCents !== neuStand.betragCents || altStand.detail !== neuStand.detail;
      positionen.push({ key: `n${nr}`, name: it.name, marke: andererBetrag ? "geaendert" : "unveraendert", alt: altStand, neu: neuStand });
    }
  }
  for (const p of vergleich.positionen.filter((x) => x.art === "entfallen")) {
    const altIt = altItems.find((it) => it.name === p.name && it.day_no === p.dayNo);
    nr += 1;
    positionen.push({ key: `e${nr}`, name: p.name, marke: "entfallen", alt: altIt ? zeilenZustand(altIt, altOption) : null, neu: null });
  }

  // Wahlgänge, bei denen die Kundin neu zuordnen muss: Gästezahl geändert oder Gang neu. Verglichen wird je Gruppe.
  const altVerz = altOption.wahlgaenge ?? {};
  const wahlgaenge = wahlgaengeAus(neuOption).filter((w) => {
    const a = altVerz[w.gruppeId];
    return !a || a.gaeste !== w.gaeste;
  });

  return {
    version: n.version,
    vorbelegung: vorbelegteMengen(offer, n.snapshot, altItems, wahlgaenge),
    vergleich,
    gaeste: vergleich.gaeste,
    datum: vergleich.datum,
    positionen,
    summe: vergleich.summe,
    wahlgaenge,
    neuOption,
    zahlung: n.zahlung ?? null,
  };
}

/** Fehlercode der Nachtrag-Antwort -> verständlicher Satz für die Kundin (Sie-Form). */
export function nachtragFehlerText(code: string | undefined, serverText?: string): string {
  switch (code) {
    case "menuwahl_unvollstaendig":
      return "Bitte ordnen Sie bei den Gängen mit Auswahl jedem Gast ein Gericht zu. Erst dann können wir die Änderung bestätigen.";
    case "wahl_menge_ungueltig":
      return "Bei einem Gericht sind mehr Gäste eingetragen, als es Gäste gibt. Bitte prüfen Sie die Anzahl.";
    case "version_veraltet":
      return "Diese Änderung wurde inzwischen ersetzt oder zurückgezogen. Wir laden die aktuelle Fassung für Sie.";
    case "rate_limited":
      return "Zu viele Anfragen. Bitte warten Sie einen Moment und versuchen Sie es erneut.";
    default:
      return serverText?.trim() || "Ihre Antwort konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.";
  }
}
